import { Link } from '@remix-run/react'
import { HeartIcon } from 'lucide-react'
import { useState } from 'react'
import Masonry from 'react-masonry-css'
import { z } from 'zod'
import { generateAnimatedImageLink, generateImageLink } from '#app/utils/mux.js'
import { CloudFrontImage } from './cloudfront-image'
import { Button } from './ui/button'
import { Dialog, DialogContent } from './ui/dialog'
import { Icon } from './ui/icon'
import { Separator } from './ui/separator'

export default function Works({
	works,
	isLoggedIn = false,
}: {
	works: {
		user: { username: string; name: string | null }
		originalAssetUrl: string
		name: string | null
		id: string
		photo: { width: number | null; height: number | null } | null
		video: {
			playbackId: string | null
			maxWidth: number | null
			maxHeight: number | null
		} | null
		collections: {
			id: string
		}[]
	}[]
	isLoggedIn: boolean
}) {
	const [modalOpen, setModalOpen] = useState<boolean>(false)
	const [worksInCollection, setWorksInCollection] = useState<string[]>(
		works
			.filter(w => w.collections && w.collections.length !== 0)
			.map(w => w.id),
	)
	if (works && works.length === 0) {
		return (
			<div className="flex max-w-prose flex-col gap-4">
				<h3 className="font-serif text-h5 leading-none">No works to display</h3>
				<p>
					Our search function is 'tag' based and requires exact word matches.
					For example searching 'Manta ray' will only bring up media that either
					includes both tags 'manta' and 'ray', or "manta ray" is in the name or
					description. Searching for 'ray' will give you results for all media
					with the tag 'ray' including manta rays, mobula rays, sting rays, etc.
					We suggest you start with broad categories such as 'ray' and then
					narrow it down.
				</p>
				<p>Please do not use filler words such as:</p>
				<ol className="list-inside list-disc">
					<li>on</li>
					<li>of</li>
					<li>the</li>
					<li>as</li>
					<li>in</li>
				</ol>
				<p>
					Our library is constantly growing and we hope to have more images that
					match your search soon.
				</p>
			</div>
		)
	}
	const addToQuickCollection = async (workId: string) => {
		const req = await fetch('/api/collections/quick', {
			method: 'POST',
			credentials: 'include',
			body: JSON.stringify({
				workId: workId,
				intent: 'add',
			}),
		})
		const res = await req.json()
		const parsed = z
			.object({
				success: z.boolean(),
				collectionId: z.string(),
				action: z.string().or(z.null()),
			})
			.safeParse(res)
		if (!parsed.success) return
		if (parsed.data.action === 'connect') {
			setWorksInCollection(works => [...works, workId])
		}
		if (parsed.data.action === 'disconnect') {
			setWorksInCollection(works => works.filter(id => id !== workId))
		}
	}
	const workMarkup = works.map((work, index) => {
		let loading: 'eager' | 'lazy' = index <= 6 ? 'eager' : 'lazy'
		let workName =
			work.name && work.name.trim() !== '' ? work.name : 'Untitled Work'
		return (
			<div
				key={`library-${work.user.username}-work-${work.id}`}
				className="group"
				onClick={() => {
					if (!isLoggedIn) setModalOpen(true)
				}}
			>
				<Link
					className={`
						mb-8 block outline-offset-8 outline-primary
						${isLoggedIn ? 'pointer-events-auto' : 'pointer-events-none'}
					`}
					to={isLoggedIn ? `/works/${work.id}` : `/login`}
					prefetch={'intent'}
				>
					<div className="relative overflow-hidden border border-black">
						{work.video?.playbackId ? (
							<>
								<img
									loading={loading}
									src={generateImageLink(work.video.playbackId)}
									alt={workName + ' poster'}
								/>
								<img
									loading={'lazy'}
									className="absolute left-0 top-0 h-full w-full opacity-0 group-hover:opacity-100 group-focus:opacity-100"
									src={generateAnimatedImageLink(work.video.playbackId)}
									alt={workName + ' animated gif'}
								/>
								<Icon
									name="video"
									className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 text-white"
								/>
							</>
						) : (
							<CloudFrontImage loading={loading} work={work} />
						)}
						{isLoggedIn && (
							<div className="absolute right-2 top-2 z-10 hidden gap-2 hover:flex focus:flex group-hover:flex group-focus:flex">
								<HeartIcon
									fill={worksInCollection.includes(work.id) ? 'red' : 'none'}
									stroke={worksInCollection.includes(work.id) ? 'red' : 'black'}
									size="1.8rem"
									onClick={e => {
										e.preventDefault()
										addToQuickCollection(work.id)
									}}
									className="rounded-md border-border bg-white p-1 text-h4 leading-none text-black opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus:opacity-100"
								/>
							</div>
						)}
					</div>
					<div className="mt-2 flex items-center gap-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
						<h3 className="font-serif text-base font-bold lg:text-lg">
							{workName}
						</h3>
						<Separator className="flex-1 bg-primary" />
					</div>
				</Link>
			</div>
		)
	})
	return (
		<>
			<Dialog
				open={modalOpen}
				onOpenChange={() => {
					setModalOpen(!open)
				}}
				defaultOpen={modalOpen}
			>
				<DialogContent className="flex max-h-[80vh] w-[calc(100vw-4rem)] max-w-screen-lg gap-0 overflow-hidden border-0 p-0 text-black md:h-[36rem] md:text-white">
					<div className="left flex flex-1 basis-1/3 flex-col gap-3 bg-[#F7F3E8] p-6 text-black md:p-8">
						<h2 className="mt-8 font-serif text-h5 font-light">
							Join Kogia&apos;s
							<br />
							Oceanic Library
						</h2>
						<p className="max-w-prose text-base">
							Apply for a free membership to access our library of photos and
							videos of the marine world, for you to use to communicate your
							work and tell your stories.
						</p>
						<Button asChild variant={'outline'} className="mt-4 font-mono">
							<Link to="/apply">Apply</Link>
						</Button>
						<div className="flex flex-1 items-end">
							<p className="w-full">
								Already have an account?{' '}
								<Link to="/login" className="text-black underline">
									Login
								</Link>
							</p>
						</div>
					</div>
					<div className="left hidden flex-1 basis-2/3 bg-secondary md:flex">
						<div className="flex flex-1 flex-col justify-end gap-3 bg-[url(/backgrounds/baitball-768.jpeg)] bg-cover p-8 pb-6 text-[#F7F3E8] sm:bg-[url(/backgrounds/baitball-1260.jpeg)] lg:bg-[url(/backgrounds/baitball-2160.jpeg)] xl:bg-[url(/backgrounds/baitball-original.jpg)]">
							<div className="flex w-3/4 flex-col gap-3 p-6">
								<h2 className="font-serif text-h5">
									A members-only library, built for conservation.
								</h2>
								<p className="max-w-[60ch] text-base">
									Our library is reserved for non-profit conservation purposes.
									It is intended for smaller conservation groups, researchers,
									scientists, activists, and artists who may not always have the
									resources to license media.
								</p>
								<p className="mb-0 max-w-[60ch] text-base">
									We encourage larger organizations to sign up as Patrons in the
									application form. The monthly contributions from Patron
									members make it possible for Kogia to continue providing media
									for smaller organizations.
								</p>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			<Masonry
				breakpointCols={{
					default: 3,
					1280: 2,
					768: 1,
				}}
				className="my-masonry-grid"
				columnClassName="my-masonry-grid_column"
			>
				{workMarkup}
			</Masonry>
		</>
	)
}
